import { TCountryCode } from 'countries-list';
import { format } from 'date-fns/fp';

const priceListService = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL : 'http://127.0.0.1:4031';

const priceListServiceGraphql = `${priceListService}/pricelist/graphql`;

interface IOfferDetailsInput {
  hotelRef: string;
  offerVersionId: number;
}

interface IQuickPriceInput {
  hotelRef: string;
  language: string;
  startDate: Date;
  endDate: Date;
  days: any[];
  serviceTypeSku: string;
  prevdayGuests: number;
  comment: string | null;
}

interface IDownloadInput extends IQuickPriceInput {
  client: {
    company: string;
    firstname: string;
    lastname: string;
    email: string;
    country: TCountryCode;
    phone: string;
    address: string;
    city: string;
    zip: string;
    billingCompany: string;
    billingFirstname: string;
    billingLastname: string;
    billingAddress: string;
    billingCity: string;
    billingZip: string;
    billingCountry: TCountryCode;
  };
}

interface IReservation {
  input: IQuickPriceInput;
  client: {
    company: string;
    firstname: string;
    lastname: string;
    email: string;
    country: TCountryCode;
    phone: string;
    address: string;
    city: string;
    zip: string;
    billingCompany: string;
    billingFirstname: string;
    billingLastname: string;
    billingAddress: string;
    billingCity: string;
    billingZip: string;
    billingCountry: TCountryCode;
  };
}

interface IServiceTypesInput {
  hotelRef: string;
  language: string;
}

interface IWidgetConfig {
  widgetSemPlan: string | null;
  widgetHotelBusinessName: string;
  widgetColor: string;
  widgetColorEnabled: boolean;
  widgetColorInherited?: string | undefined;
  widgetPrivacyUrl: string;
  widgetTermsUrl: string;
  widgetNoRooms: boolean;
  widgetExpandedMode: boolean;
  widgetSlimMode: boolean;
  maintenanceMode: boolean;
}

/**
 * Approves an offer version from the customer-facing flow.
 * @param offerVersionId Offer version identifier.
 * @returns API response payload or `null` if missing.
 */
export async function approveOfferVersion(offerVersionId: number) {
  let res = await fetch(priceListServiceGraphql, {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      query: `mutation ApproveOfferVersionClient($offerVersionId: Int!) {
        approveOfferVersionClient(offerVersionId: $offerVersionId)
      }`,
      variables: {
        offerVersionId: offerVersionId,
      },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0].message, { cause: json.errors });
  } else if (json.data && json.data.approveOfferVersionClient) {
    return json.data.approveOfferVersionClient;
  } else {
    return null;
  }
}

/**
 * Rejects an existing offer version from the customer-facing flow.
 * @param offerVersionId Offer version identifier.
 * @returns API response payload or `null` if missing.
 */
export async function rejectOfferVersion(offerVersionId: number) {
  let res = await fetch(priceListServiceGraphql, {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      query: `mutation RejectOfferVersionClient($offerVersionId: Int!) {
        rejectOfferVersionClient(offerVersionId: $offerVersionId)
      }`,
      variables: {
        offerVersionId: offerVersionId,
      },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0].message, { cause: json.errors });
  } else if (json.data && json.data.rejectOfferVersionClient) {
    return json.data.rejectOfferVersionClient;
  } else {
    return null;
  }
}

/**
 * Fetches an HTML preview for the current booking input.
 * @param reservation Reservation payload with pricing input and contact data.
 * @returns HTML preview object or `null`.
 */
export async function getHTMLOfferPreview(reservation: IReservation) {
  let res = await fetch(priceListServiceGraphql, {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      query: `query OfferQuickInputHtmlPreview($hotelRef: String!, $input: QuickPriceInput!, $client: QuickPriceClient!) {
        offerQuickInputHtmlPreview(hotelRef: $hotelRef, input: $input, client: $client, source: WIDGET) {
          html
        }
      }`,
      variables: {
        hotelRef: reservation.input.hotelRef,
        input: {
          language: reservation.input.language,
          startDate: format('yyyy-MM-dd', reservation.input.startDate),
          endDate: format('yyyy-MM-dd', reservation.input.endDate),
          days: reservation.input.days,
          prevdayGuests: reservation.input.prevdayGuests,
          serviceTypeSku: reservation.input.serviceTypeSku,
          comment: reservation.input.comment,
        },
        client: reservation.client,
      },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0].message, { cause: json.errors });
  } else if (json.data && json.data.offerQuickInputHtmlPreview) {
    return json.data.offerQuickInputHtmlPreview;
  } else {
    return null;
  }
}

/**
 * Loads a persisted offer and its change markers.
 * @param offerDetailsInput Hotel and offer version identifiers.
 * @returns Offer details or `null`.
 */
export async function getOfferDetails(offerDetailsInput: IOfferDetailsInput) {
  let res = await fetch(priceListServiceGraphql, {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      query: `query OfferDetails($hotelRef: String!, $input: OfferDetailsInput!) {
        offerDetails(hotelRef: $hotelRef, input: $input) {
          input {
            days {
              addons {
                sku
                count
              }
            }
          }
          html
          htmlWithChanges
          actionRequired
        }
      }`,
      variables: {
        hotelRef: offerDetailsInput.hotelRef,
        input: {
          offerVersionId: offerDetailsInput.offerVersionId,
        },
      },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0].message, { cause: json.errors });
  } else if (json.data && json.data.offerDetails) {
    return json.data.offerDetails;
  } else {
    return null;
  }
}

/**
 * Loads widget runtime defaults for a hotel reference.
 * @param hotelRef Unique hotel reference.
 * @returns Widget configuration for theming and feature flags.
 */
export async function getWidgetConfig(hotelRef?: string): Promise<IWidgetConfig | null> {
  if (!hotelRef) throw new Error('hotelRef is required');
  let res = await fetch(priceListServiceGraphql, {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      query: `query WidgetConfigQuery($hotelRef: String!) {
        widgetConfig(hotelRef: $hotelRef) {
          widgetSemPlan
          widgetHotelBusinessName
          widgetColor
          widgetColorEnabled
          widgetColorInherited
          widgetPrivacyUrl
          widgetTermsUrl
          widgetNoRooms
          widgetExpandedMode
          widgetSlimMode
          maintenanceMode
        }
      }`,
      variables: {
        hotelRef: hotelRef,
      },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0].message, { cause: json.errors });
  } else if (json.data && json.data.widgetConfig) {
    return json.data.widgetConfig;
  } else {
    return null;
  }
}

/**
 * Loads service types and hydration payloads for local pricing/availability engines.
 * @param servicesTypesInput Hotel reference and language.
 * @returns Service type list and dehydrated domain data or `null`.
 */
export async function getServiceTypes(servicesTypesInput: IServiceTypesInput) {
  let res = await fetch(priceListServiceGraphql, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: `query ListServiceTypes($hotelRef: String!, $language: ELanguageCode) {
        quickPriceDehydrate(hotelRef: $hotelRef, source: WIDGET)
        availabilityDehydrate(hotelRef: $hotelRef)
        listServiceTypes(hotelRef: $hotelRef, language: $language, source: WIDGET) {
          addonFacilities {
            sku
            name
            header
            details
            lookupCode
            isAddon
            recurring
          }
          addonProducts {
            sku
            name
            header
            details
            isAddon
            isDeduction
            lookupCode
            recurring
          }
          details
          header
          headerShort
          maxDayVisitors
          maxOvernight
          minDayVisitors
          minOvernight
          name
          sku
        }
      }`,
      variables: {
        hotelRef: servicesTypesInput.hotelRef,
        language: servicesTypesInput.language,
      },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0].message, { cause: json.errors });
  } else if (json.data && json.data.listServiceTypes) {
    return {
      serviceTypes: json.data.listServiceTypes,
      availabilityDehydrate: json.data.availabilityDehydrate,
      quickPriceDehydrate: json.data.quickPriceDehydrate,
    };
  } else {
    return null;
  }
}

/**
 * Creates a reservation request from the widget checkout flow.
 * @param reservation Complete reservation input and client data.
 * @returns Reservation response (including email verification requirement) or `null`.
 */
export const placeReservation = async (reservation: IReservation) => {
  let res = await fetch(priceListServiceGraphql, {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      query: `mutation PlaceReservation($hotelRef: String!, $input: QuickPriceInput!, $client: QuickPriceClient!) {
        placeReservation(hotelRef: $hotelRef, input: $input, client: $client) {
          cancelUrl
          emailVerificationRequired
          refCode
          viewUrl
        }
      }`,
      variables: {
        hotelRef: reservation.input.hotelRef,
        input: {
          language: reservation.input.language,
          startDate: reservation.input.startDate,
          endDate: reservation.input.endDate,
          days: reservation.input.days,
          prevdayGuests: reservation.input.prevdayGuests,
          serviceTypeSku: reservation.input.serviceTypeSku,
          comment: reservation.input.comment,
        },
        client: reservation.client,
      },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0].message, { cause: json.errors });
  } else if (json.data && json.data.placeReservation) {
    return json.data.placeReservation;
  } else {
    return null;
  }
};

/**
 * Verifies the email confirmation code for pending reservations.
 * @param hotelRef Hotel reference of the pending reservation.
 * @param code Verification code from customer email.
 * @returns `true` when the code is accepted by the backend.
 */
export const verifyReservationEmail = async (hotelRef: string, code: string): Promise<boolean> => {
  let res = await fetch(priceListServiceGraphql, {
    method: 'POST',

    headers: {
      'Content-Type': 'application/json',
    },

    body: JSON.stringify({
      query: `mutation VerifyReservationEmail($hotelRef: String!, $code: String!) {
        verifyReservationEmail(hotelRef: $hotelRef, code: $code)
      }`,
      variables: {
        hotelRef: hotelRef,
        code: code,
      },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0].message, { cause: json.errors });
  } else if (json.data && json.data.verifyReservationEmail) {
    return !!json.data.verifyReservationEmail;
  } else {
    return false;
  }
};

/**
 * Builds a quick-print URL with serialized reservation data.
 * @param quickPriceInput Reservation and client payload.
 * @returns Fully qualified URL for PDF download endpoint.
 */
export const getDownloadUrl = (quickPriceInput: IDownloadInput) => {
  const query = new URLSearchParams({
    data: JSON.stringify({
      ...quickPriceInput,
      startDate: format('yyyy-MM-dd', quickPriceInput.startDate),
      endDate: format('yyyy-MM-dd', quickPriceInput.endDate),
    }),
  }).toString();
  return `${priceListService}/pricelist/quickprint/widget/${quickPriceInput.hotelRef}?${query}`;
};
